import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error(typeof detail === "string" ? detail : "Slow down — try again in a minute.");
      } else {
        // Always succeed visually to avoid enumeration.
        setSent(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="flex-1 mx-auto max-w-md w-full px-4 py-16">
        <h1 className="font-display text-4xl mb-2" data-testid="forgot-title">Forgot password?</h1>
        <p className="text-ink/70 mb-8">
          Enter your email. If we have an account on file, a reset link is on the way. It expires in one hour.
        </p>
        {sent ? (
          <div className="rounded-lg border border-line bg-panel p-5 flex items-start gap-3" data-testid="forgot-sent">
            <Mail size={20} className="text-gold mt-0.5" />
            <div>
              <p className="font-semibold">Check your inbox.</p>
              <p className="text-sm text-ink/70 mt-1">
                If <span className="font-mono text-ink">{email}</span> matches an ArgusSecure account, you'll receive a reset link within a minute.
              </p>
              <Link to="/login" className="text-sm text-gold underline mt-3 inline-block">
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-ink/60">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-paper border-line text-ink h-12"
                data-testid="forgot-email"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-gold text-paper hover:bg-gold2 h-12 font-semibold"
              data-testid="forgot-submit"
            >
              {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</> : "Send reset link"}
            </Button>
            <p className="text-sm text-ink/60 text-center">
              <Link to="/login" className="text-gold underline">Back to login</Link>
            </p>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
